import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import sanitizeText from '@/utils/sanitizeText';
import {
  FaShieldAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSync,
  FaSave,
  FaTimes,
  FaToggleOn,
  FaToggleOff,
  FaExclamationTriangle,
  FaLock,
  FaInfoCircle,
  FaRobot,
  FaCheck,
  FaBan,
  FaLightbulb
} from 'react-icons/fa';
import './RouterFirewallTemplatesPage.scss';
import * as Sentry from '@sentry/react';

/**
 * Redesigned Router Firewall Templates Page
 * 
 * Modern table-based interface matching RouterFirewallEditor UX.
 * Manages default firewall rules applied during router provisioning.
 * 
 * Features:
 * - Inline editing (click to edit)
 * - Visual toggles for enable/disable
 * - Color-coded actions
 * - Priority ordering with drag-drop
 * - Category badges
 * - Mandatory rule protection
 */
const RouterFirewallTemplatesPage = () => {
  const { apiFetch } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  
  // Editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplateForm, setNewTemplateForm] = useState({
    ruleName: '',
    description: '',
    chain: 'input',
    action: 'accept',
    sourceAddress: '',
    destinationAddress: '',
    protocol: '',
    sourcePort: '',
    destinationPort: '',
    connectionState: '',
    inInterface: '',
    outInterface: '',
    comment: '',
    customParameters: '',
    isEnabled: true,
    priority: 100,
    category: 'Security',
    isMandatory: false
  });

  // AI Analysis
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [rejectingIndex, setRejectingIndex] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // AI modal — bulk suggestion selection and apply state
  const [selectedSuggestionIndices, setSelectedSuggestionIndices] = useState(new Set());
  const [applyingBulk, setApplyingBulk] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  // ===== Data Loading =====
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/RouterConfig/firewall-templates');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Sort by priority
          const sorted = (data.data || []).sort((a, b) => (a.priority || 100) - (b.priority || 100));
          setTemplates(sorted);
          setLastFetched(new Date());
        } else {
          setError(data.error || 'Failed to load firewall templates');
        }
      } else {
        setError('Failed to load firewall templates');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading firewall templates:', err);
      setError('Failed to load firewall templates');
    } finally {
      setLoading(false);
    }
  };

  // ===== AI Analysis =====
  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const response = await apiFetch('/api/RouterConfig/firewall-templates/ai-analysis', { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setAiResult(data.data);
        setShowAiModal(true);
      } else {
        toast.error(data.error || 'AI analysis failed');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('AI analysis error:', err);
      toast.error('Failed to run AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApproveSuggestion = async (suggestion, index) => {
    try {
      const response = await apiFetch('/api/RouterConfig/firewall-templates/ai-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ruleName: suggestion.title || 'AI-Suggested Rule',
          description: suggestion.description,
          chain: suggestion.chain || 'input',
          action: suggestion.action || 'drop',
          sourceAddress: suggestion.sourceAddress || '',
          destinationAddress: suggestion.destinationAddress || '',
          protocol: suggestion.protocol || '',
          sourcePort: suggestion.sourcePort || '',
          destinationPort: suggestion.destinationPort || '',
          connectionState: suggestion.connectionState || '',
          comment: suggestion.comment || suggestion.title,
          customParameters: '',
          priority: suggestion.priority || 100,
          category: suggestion.category || 'Security'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Suggestion approved');
        // Mark as approved in local state
        setAiResult(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated.suggestions = [...(updated.suggestions || [])];
          updated.suggestions[index] = { ...updated.suggestions[index], _approved: true };
          return updated;
        });
        loadTemplates(); // Refresh templates list
      } else {
        toast.error(data.error || 'Failed to approve suggestion');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Approve error:', err);
      toast.error('Failed to approve suggestion');
    }
  };

  const handleRejectSuggestion = async (suggestion, index) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      const response = await apiFetch('/api/RouterConfig/firewall-templates/ai-reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          suggestionTitle: suggestion.title,
          suggestedRule: suggestion.suggestedRule || suggestion.title,
          reason: rejectReason.trim()
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Rejection recorded and added to knowledge base');
        setRejectingIndex(null);
        setRejectReason('');
        // Mark as rejected in local state
        setAiResult(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated.suggestions = [...(updated.suggestions || [])];
          updated.suggestions[index] = { ...updated.suggestions[index], _rejected: true };
          return updated;
        });
      } else {
        toast.error(data.error || 'Failed to record rejection');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Reject error:', err);
      toast.error('Failed to record rejection');
    }
  };

  // ===== AI Suggestion Selection Helpers =====

  /**
   * Toggles selection of a single suggestion by index in the AI Analysis modal.
   */
  const toggleSuggestionSelection = (index) => {
    setSelectedSuggestionIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  /**
   * Selects or deselects all pending (not yet approved/rejected) suggestions.
   */
  const toggleAllSuggestions = () => {
    const pending = (aiResult?.suggestions || [])
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !s._approved && !s._rejected)
      .map(({ i }) => i);

    if (selectedSuggestionIndices.size === pending.length) {
      setSelectedSuggestionIndices(new Set());
    } else {
      setSelectedSuggestionIndices(new Set(pending));
    }
  };

  /**
   * Bulk-approves the supplied suggestion indices: sends them to the bulk-approve endpoint
   * which persists each as a firewall template, records a knowledge-base entry, and
   * deploys them to all provisioned routers with safe-mode monitoring.
   *
   * @param {number[]} indices - Array of suggestion indices to approve.
   */
  const handleApplySuggestions = async (indices) => {
    if (indices.length === 0) {
      toast.error('No suggestions selected');
      return;
    }

    const suggestions = aiResult?.suggestions || [];
    const toApprove = indices
      .map(i => suggestions[i])
      .filter(s => s && !s._approved && !s._rejected);

    if (toApprove.length === 0) {
      toast.error('All selected suggestions have already been approved or rejected');
      return;
    }

    const confirmMsg = `Apply ${toApprove.length} AI suggestion(s) as firewall templates?\n\nSafe Mode will be enabled on all routers. Each router is monitored for 2 minutes before safe mode is confirmed. If connectivity is lost the router will auto-revert.`;
    if (!window.confirm(confirmMsg)) return;

    setApplyingBulk(true);
    try {
      const approvals = toApprove.map(s => ({
        ruleName: s.title || 'AI-Suggested Rule',
        description: s.description || '',
        chain: s.chain || 'input',
        action: s.action || 'drop',
        sourceAddress: s.sourceAddress || '',
        destinationAddress: s.destinationAddress || '',
        protocol: s.protocol || '',
        sourcePort: s.sourcePort || '',
        destinationPort: s.destinationPort || '',
        connectionState: s.connectionState || '',
        comment: s.comment || s.title,
        customParameters: '',
        priority: s.priority || 100,
        category: s.category || 'Security'
      }));

      const response = await apiFetch('/api/RouterConfig/firewall-templates/ai-approve-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvals })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || `${toApprove.length} suggestion(s) approved and queued for deployment`);

        // Mark only the suggestions that were actually included in the approvals payload
        // (i.e., the ones that were still pending). Marking every raw index would
        // incorrectly flip previously rejected or already-approved suggestions to approved.
        setAiResult(prev => {
          if (!prev) return prev;
          const updated = { ...prev, suggestions: [...(prev.suggestions || [])] };
          indices.forEach(i => {
            const suggestion = updated.suggestions[i];
            if (suggestion && !suggestion._approved && !suggestion._rejected) {
              updated.suggestions[i] = { ...suggestion, _approved: true };
            }
          });
          return updated;
        });
        setSelectedSuggestionIndices(new Set());
        loadTemplates();
      } else {
        toast.error(data.error || 'Failed to apply suggestions');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Bulk apply error:', err);
      toast.error('Failed to apply suggestions');
    } finally {
      setApplyingBulk(false);
    }
  };

  // ===== Validation =====
  const isValidIPAddressOrCIDR = (value) => {
    if (!value || value === '' || value === 'any') return true;
    const parts = value.split('/');
    const ip = parts[0];
    const octets = ip.split('.');
    if (octets.length !== 4) return false;
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (isNaN(num) || num < 0 || num > 255) return false;
    }
    if (parts.length === 2) {
      const cidr = parseInt(parts[1], 10);
      if (isNaN(cidr) || cidr < 0 || cidr > 32) return false;
    }
    return true;
  };

  const isValidPort = (value) => {
    if (!value || value === '' || value === 'any') return true;
    if (value.includes('-')) {
      const [s, e] = value.split('-').map(v => parseInt(v, 10));
      return !isNaN(s) && !isNaN(e) && s >= 1 && s <= 65535 && e >= 1 && e <= 65535 && s < e;
    }
    const port = parseInt(value, 10);
    return !isNaN(port) && port >= 1 && port <= 65535;
  };

  const isValidProtocol = (value) => {
    if (!value || value === '') return true;
    return ['tcp', 'udp', 'icmp', 'gre', 'esp', 'ah', 'ipencap', 'ospf'].includes(value.toLowerCase());
  };

  const isValidChain = (value) => {
    return ['input', 'forward', 'output'].includes(value?.toLowerCase());
  };

  const isValidAction = (value) => {
    return ['accept', 'drop', 'reject', 'tarpit', 'passthrough', 'return', 'jump', 'log'].includes(value?.toLowerCase());
  };

  const isValidConnectionState = (value) => {
    if (!value || value === '') return true;
    return ['established', 'related', 'new', 'invalid', 'untracked'].includes(value.toLowerCase());
  };

  // ===== Inline Editing =====
  const startEdit = (template) => {
    setEditingId(template.id);
    setEditForm({ ...template });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    // Validation
    if (!editForm.ruleName || !editForm.chain || !editForm.action) {
      toast.error('Rule name, chain, and action are required');
      return;
    }

    if (editForm.sourceAddress && !isValidIPAddressOrCIDR(editForm.sourceAddress)) {
      toast.error('Invalid source address format');
      return;
    }

    if (editForm.destinationAddress && !isValidIPAddressOrCIDR(editForm.destinationAddress)) {
      toast.error('Invalid destination address format');
      return;
    }

    if (editForm.sourcePort && !isValidPort(editForm.sourcePort)) {
      toast.error('Invalid source port');
      return;
    }

    if (editForm.destinationPort && !isValidPort(editForm.destinationPort)) {
      toast.error('Invalid destination port');
      return;
    }

    if (editForm.protocol && !isValidProtocol(editForm.protocol)) {
      toast.error('Invalid protocol');
      return;
    }

    if (!isValidChain(editForm.chain)) {
      toast.error('Invalid chain');
      return;
    }

    if (!isValidAction(editForm.action)) {
      toast.error('Invalid action');
      return;
    }

    if (editForm.connectionState && !isValidConnectionState(editForm.connectionState)) {
      toast.error('Invalid connection state');
      return;
    }

    try {
      const response = await apiFetch(`/api/RouterConfig/firewall-templates/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('Template updated and synced to all routers');
          cancelEdit();
          await loadTemplates();
        } else {
          toast.error(data.error || 'Failed to update template');
        }
      } else {
        toast.error('Failed to update template');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error updating template:', err);
      toast.error('Failed to update template');
    }
  };

  // ===== Toggle Enable/Disable =====
  const toggleEnabled = async (template) => {
    try {
      const updated = { ...template, isEnabled: !template.isEnabled };
      const response = await apiFetch(`/api/RouterConfig/firewall-templates/${template.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(`Template ${updated.isEnabled ? 'enabled' : 'disabled'}`);
          setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, isEnabled: updated.isEnabled } : t));
        } else {
          toast.error(data.error || 'Failed to toggle template');
        }
      } else {
        toast.error('Failed to toggle template');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error toggling template:', err);
      toast.error('Failed to toggle template');
    }
  };

  // ===== Delete =====
  const deleteTemplate = async (template) => {
    if (template.isMandatory) {
      toast.error('Cannot delete mandatory templates');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${template.ruleName}"?`)) {
      return;
    }

    try {
      const response = await apiFetch(`/api/RouterConfig/firewall-templates/${template.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(data.message || 'Template deleted and synced to all routers');
          await loadTemplates();
        } else {
          toast.error(data.error || 'Failed to delete template');
        }
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error deleting template:', err);
      toast.error('Failed to delete template');
    }
  };

  // ===== Add New Template =====
  const handleAddChange = (field, value) => {
    setNewTemplateForm(prev => ({ ...prev, [field]: value }));
  };

  const saveNewTemplate = async () => {
    // Validation
    if (!newTemplateForm.ruleName || !newTemplateForm.chain || !newTemplateForm.action) {
      toast.error('Rule name, chain, and action are required');
      return;
    }

    try {
      const response = await apiFetch('/api/RouterConfig/firewall-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTemplateForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('Template created and synced to all routers');
          setShowAddModal(false);
          setNewTemplateForm({
            ruleName: '',
            description: '',
            chain: 'input',
            action: 'accept',
            sourceAddress: '',
            destinationAddress: '',
            protocol: '',
            sourcePort: '',
            destinationPort: '',
            connectionState: '',
            inInterface: '',
            outInterface: '',
            comment: '',
            customParameters: '',
            isEnabled: true,
            priority: 100,
            category: 'Security',
            isMandatory: false
          });
          await loadTemplates();
        } else {
          toast.error(data.error || 'Failed to create template');
        }
      } else {
        toast.error('Failed to create template');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error creating template:', err);
      toast.error('Failed to create template');
    }
  };

  // ===== Action Color =====
  const getActionColor = (action) => {
    const a = action?.toLowerCase();
    if (a === 'accept') return 'action-accept';
    if (a === 'drop' || a === 'reject') return 'action-drop';
    if (a === 'tarpit') return 'action-tarpit';
    return 'action-other';
  };

  // ===== Category Badge =====
  const getCategoryBadge = (category) => {
    const badges = {
      'Security': '🔒',
      'NAT': '🔀',
      'QoS': '📊',
      'Custom': '⚙️'
    };
    return badges[category] || '📋';
  };

  // ===== Priority Color =====
  const getPriorityColor = (priority) => {
    if (priority <= 20) return 'priority-high';
    if (priority <= 50) return 'priority-medium';
    return 'priority-low';
  };

  if (loading) {
    return (
      <div className="settings-container">
        <HexagonLoader />
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="header-title">
          <FaShieldAlt className="header-icon" />
          <h1>Default Firewall Templates</h1>
        </div>
        <p className="header-description">
          Configure default firewall rules automatically deployed during router provisioning.
          Any change (add, edit, or delete) is automatically synced to all provisioned routers
          with safe mode enabled — routers auto-revert if connectivity is lost.
        </p>
      </div>

      {error && (
        <div className="error-message">
          <FaExclamationTriangle /> {sanitizeText(error)}
        </div>
      )}

      <div className="firewall-templates-modern">
        {/* Header Actions */}
        <div className="templates-header-actions">
          <div className="header-info">
            <span className="templates-count">
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </span>
            {lastFetched && (
              <span className="last-fetched">
                Last updated: {lastFetched.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="header-buttons">
            <button
              className="btn btn-secondary"
              onClick={runAiAnalysis}
              disabled={aiLoading || templates.length === 0}
              title="Run AI analysis on your firewall templates"
            >
              {aiLoading ? <><FaSync className="fa-spin" /> Analysing...</> : <><FaRobot /> AI Analysis</>}
            </button>
            <button className="btn btn-secondary" onClick={loadTemplates}>
              <FaSync /> Refresh
            </button>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <FaPlus /> Add Template
            </button>
          </div>
        </div>

        {/* Templates Table */}
        <div className="templates-table-container">
          <table className="templates-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>Pri</th>
                <th style={{ width: '50px' }}>Cat</th>
                <th style={{ width: '80px' }}>Status</th>
                <th style={{ width: '120px' }}>Rule Name</th>
                <th style={{ width: '100px' }}>Chain</th>
                <th style={{ width: '100px' }}>Action</th>
                <th style={{ width: '80px' }}>Protocol</th>
                <th style={{ width: '130px' }}>Source</th>
                <th style={{ width: '130px' }}>Destination</th>
                <th style={{ width: '250px' }}>Comment</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan="11" className="no-data">
                    <FaInfoCircle /> No templates configured. Click "Add Template" to create one.
                  </td>
                </tr>
              ) : (
                templates.map((template) => {
                  const isEditing = editingId === template.id;
                  const current = isEditing ? editForm : template;

                  return (
                    <tr key={template.id} className={isEditing ? 'editing' : ''}>
                      {/* Priority */}
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={current.priority || 100}
                            onChange={(e) => handleEditChange('priority', parseInt(e.target.value, 10))}
                            min="0"
                            max="999"
                            className="inline-edit-input"
                          />
                        ) : (
                          <span className={`priority-badge ${getPriorityColor(template.priority || 100)}`}>
                            {template.priority || 100}
                          </span>
                        )}
                      </td>

                      {/* Category */}
                      <td>
                        {isEditing ? (
                          <select
                            value={current.category || 'Security'}
                            onChange={(e) => handleEditChange('category', e.target.value)}
                            className="inline-edit-select"
                          >
                            <option value="Security">Security</option>
                            <option value="NAT">NAT</option>
                            <option value="QoS">QoS</option>
                            <option value="Custom">Custom</option>
                          </select>
                        ) : (
                          <span className="category-badge" title={template.category}>
                            {getCategoryBadge(template.category)}
                            {template.isMandatory && <FaLock className="mandatory-icon" title="Mandatory" />}
                          </span>
                        )}
                      </td>

                      {/* Status Toggle */}
                      <td>
                        <button
                          className={`status-toggle ${current.isEnabled ? 'enabled' : 'disabled'}`}
                          onClick={() => !isEditing && toggleEnabled(template)}
                          disabled={isEditing}
                          title={current.isEnabled ? 'Enabled' : 'Disabled'}
                        >
                          {current.isEnabled ? <FaToggleOn /> : <FaToggleOff />}
                          <span>{current.isEnabled ? 'ON' : 'OFF'}</span>
                        </button>
                      </td>

                      {/* Rule Name */}
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={current.ruleName || ''}
                            onChange={(e) => handleEditChange('ruleName', e.target.value)}
                            className="inline-edit-input"
                            placeholder="Rule name"
                          />
                        ) : (
                          <strong>{template.ruleName}</strong>
                        )}
                      </td>

                      {/* Chain */}
                      <td>
                        {isEditing ? (
                          <select
                            value={current.chain || 'input'}
                            onChange={(e) => handleEditChange('chain', e.target.value)}
                            className="inline-edit-select"
                          >
                            <option value="input">input</option>
                            <option value="forward">forward</option>
                            <option value="output">output</option>
                          </select>
                        ) : (
                          <code>{template.chain}</code>
                        )}
                      </td>

                      {/* Action */}
                      <td>
                        {isEditing ? (
                          <select
                            value={current.action || 'accept'}
                            onChange={(e) => handleEditChange('action', e.target.value)}
                            className="inline-edit-select"
                          >
                            <option value="accept">accept</option>
                            <option value="drop">drop</option>
                            <option value="reject">reject</option>
                            <option value="tarpit">tarpit</option>
                            <option value="passthrough">passthrough</option>
                            <option value="return">return</option>
                            <option value="jump">jump</option>
                            <option value="log">log</option>
                          </select>
                        ) : (
                          <span className={`action-badge ${getActionColor(template.action)}`}>
                            {template.action}
                          </span>
                        )}
                      </td>

                      {/* Protocol */}
                      <td>
                        {isEditing ? (
                          <select
                            value={current.protocol || ''}
                            onChange={(e) => handleEditChange('protocol', e.target.value)}
                            className="inline-edit-select"
                          >
                            <option value="">any</option>
                            <option value="tcp">tcp</option>
                            <option value="udp">udp</option>
                            <option value="icmp">icmp</option>
                            <option value="gre">gre</option>
                            <option value="esp">esp</option>
                          </select>
                        ) : (
                          <code>{template.protocol || 'any'}</code>
                        )}
                      </td>

                      {/* Source */}
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={current.sourceAddress || ''}
                            onChange={(e) => handleEditChange('sourceAddress', e.target.value)}
                            className="inline-edit-input"
                            placeholder="any"
                          />
                        ) : (
                          <code className="address-display">{template.sourceAddress || 'any'}</code>
                        )}
                        {!isEditing && template.sourcePort && (
                          <div className="port-display">:{template.sourcePort}</div>
                        )}
                      </td>

                      {/* Destination */}
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={current.destinationAddress || ''}
                            onChange={(e) => handleEditChange('destinationAddress', e.target.value)}
                            className="inline-edit-input"
                            placeholder="any"
                          />
                        ) : (
                          <code className="address-display">{template.destinationAddress || 'any'}</code>
                        )}
                        {!isEditing && template.destinationPort && (
                          <div className="port-display">:{template.destinationPort}</div>
                        )}
                      </td>

                      {/* Comment */}
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={current.comment || ''}
                            onChange={(e) => handleEditChange('comment', e.target.value)}
                            className="inline-edit-input"
                            placeholder="Optional comment"
                          />
                        ) : (
                          <span className="comment-text">{template.comment}</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="action-buttons">
                        {isEditing ? (
                          <>
                            <button className="btn-icon btn-save" onClick={saveEdit} title="Save">
                              <FaSave />
                            </button>
                            <button className="btn-icon btn-cancel" onClick={cancelEdit} title="Cancel">
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn-icon btn-edit" onClick={() => startEdit(template)} title="Edit">
                              <FaEdit />
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => deleteTemplate(template)}
                              disabled={template.isMandatory}
                              title={template.isMandatory ? 'Mandatory - Cannot delete' : 'Delete'}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="templates-legend">
          <span><FaLock /> Mandatory (cannot be deleted)</span>
          <span>🔒 Security | 🔀 NAT | 📊 QoS | ⚙️ Custom</span>
          <span className="priority-badge priority-high">1-20</span>
          <span className="priority-badge priority-medium">21-50</span>
          <span className="priority-badge priority-low">51+</span>
        </div>
      </div>

      {/* Add Template Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content add-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FaPlus /> Add Firewall Template</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Rule Name *</label>
                  <input
                    type="text"
                    value={newTemplateForm.ruleName}
                    onChange={(e) => handleAddChange('ruleName', e.target.value)}
                    placeholder="e.g., Allow SNMP Access"
                  />
                </div>

                <div className="form-group">
                  <label>Priority (0-999)</label>
                  <input
                    type="number"
                    value={newTemplateForm.priority}
                    onChange={(e) => handleAddChange('priority', parseInt(e.target.value, 10))}
                    min="0"
                    max="999"
                  />
                  <small>Lower numbers = higher priority</small>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newTemplateForm.category}
                    onChange={(e) => handleAddChange('category', e.target.value)}
                  >
                    <option value="Security">Security</option>
                    <option value="NAT">NAT</option>
                    <option value="QoS">QoS</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Chain *</label>
                  <select
                    value={newTemplateForm.chain}
                    onChange={(e) => handleAddChange('chain', e.target.value)}
                  >
                    <option value="input">input</option>
                    <option value="forward">forward</option>
                    <option value="output">output</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Action *</label>
                  <select
                    value={newTemplateForm.action}
                    onChange={(e) => handleAddChange('action', e.target.value)}
                  >
                    <option value="accept">accept</option>
                    <option value="drop">drop</option>
                    <option value="reject">reject</option>
                    <option value="tarpit">tarpit</option>
                    <option value="passthrough">passthrough</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Protocol</label>
                  <select
                    value={newTemplateForm.protocol}
                    onChange={(e) => handleAddChange('protocol', e.target.value)}
                  >
                    <option value="">any</option>
                    <option value="tcp">tcp</option>
                    <option value="udp">udp</option>
                    <option value="icmp">icmp</option>
                    <option value="gre">gre</option>
                    <option value="esp">esp</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Source Address</label>
                  <input
                    type="text"
                    value={newTemplateForm.sourceAddress}
                    onChange={(e) => handleAddChange('sourceAddress', e.target.value)}
                    placeholder="e.g., 192.168.1.0/24 or any"
                  />
                </div>

                <div className="form-group">
                  <label>Source Port</label>
                  <input
                    type="text"
                    value={newTemplateForm.sourcePort}
                    onChange={(e) => handleAddChange('sourcePort', e.target.value)}
                    placeholder="e.g., 8080 or 8000-9000"
                  />
                </div>

                <div className="form-group">
                  <label>Destination Address</label>
                  <input
                    type="text"
                    value={newTemplateForm.destinationAddress}
                    onChange={(e) => handleAddChange('destinationAddress', e.target.value)}
                    placeholder="e.g., 10.0.0.1 or any"
                  />
                </div>

                <div className="form-group">
                  <label>Destination Port</label>
                  <input
                    type="text"
                    value={newTemplateForm.destinationPort}
                    onChange={(e) => handleAddChange('destinationPort', e.target.value)}
                    placeholder="e.g., 443 or 1024-65535"
                  />
                </div>

                <div className="form-group">
                  <label>Connection State</label>
                  <select
                    value={newTemplateForm.connectionState}
                    onChange={(e) => handleAddChange('connectionState', e.target.value)}
                  >
                    <option value="">any</option>
                    <option value="established">established</option>
                    <option value="related">related</option>
                    <option value="new">new</option>
                    <option value="invalid">invalid</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>In Interface</label>
                  <input
                    type="text"
                    value={newTemplateForm.inInterface}
                    onChange={(e) => handleAddChange('inInterface', e.target.value)}
                    placeholder="e.g., ether1 or any"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Comment</label>
                  <textarea
                    value={newTemplateForm.comment}
                    onChange={(e) => handleAddChange('comment', e.target.value)}
                    placeholder="Optional description of this rule"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newTemplateForm.isEnabled}
                      onChange={(e) => handleAddChange('isEnabled', e.target.checked)}
                    />
                    <span>Enabled</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newTemplateForm.isMandatory}
                      onChange={(e) => handleAddChange('isMandatory', e.target.checked)}
                    />
                    <span>Mandatory (cannot be removed)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveNewTemplate}>
                <FaSave /> Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {showAiModal && aiResult && (() => {
        // Compute pending (not yet approved/rejected) suggestion indices for the "select all" checkbox.
        const pendingSuggestions = (aiResult.suggestions || [])
          .map((s, i) => ({ s, i }))
          .filter(({ s }) => !s._approved && !s._rejected);
        const allPendingSelected = pendingSuggestions.length > 0
          && pendingSuggestions.every(({ i }) => selectedSuggestionIndices.has(i));

        return (
          <div className="modal-overlay" onClick={() => { setShowAiModal(false); setSelectedSuggestionIndices(new Set()); }}>
            <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3><FaRobot /> AI Firewall Analysis</h3>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowAiModal(false); setSelectedSuggestionIndices(new Set()); }}>
                  <FaTimes /> Close
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {/* Summary */}
                {aiResult.summary && (
                  <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f8f9fa', borderRadius: 6, borderLeft: '4px solid #495057' }}>
                    <strong>Summary:</strong> {aiResult.summary}
                    {aiResult.score != null && (
                      <span style={{
                        marginLeft: 12,
                        fontWeight: 700,
                        color: aiResult.score >= 80 ? '#2e7d32' : aiResult.score >= 60 ? '#ffa000' : '#d32f2f'
                      }}>
                        Score: {aiResult.score}/100
                      </span>
                    )}
                  </div>
                )}

                {/* Suggestions */}
                {(aiResult.suggestions || []).length > 0 ? (
                  <div>
                    {/* Section header with select-all checkbox */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        onChange={toggleAllSuggestions}
                        disabled={pendingSuggestions.length === 0}
                        title="Select / deselect all pending suggestions"
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <h4 style={{ margin: 0 }}>
                        <FaLightbulb style={{ marginRight: 6, color: '#ffa000' }} />
                        Suggestions ({aiResult.suggestions.length})
                      </h4>
                    </div>

                    {aiResult.suggestions.map((s, i) => {
                      const severityColor = s.severity === 'critical' ? '#d32f2f'
                        : s.severity === 'high' ? '#ff5722'
                        : s.severity === 'medium' ? '#ffa000'
                        : s.severity === 'low' ? '#7cb342' : '#999';
                      const isApproved = s._approved;
                      const isRejected = s._rejected;
                      const isRejecting = rejectingIndex === i;
                      const isSelected = selectedSuggestionIndices.has(i);

                      return (
                        <div key={i} style={{
                          border: '1px solid #ddd',
                          borderLeft: `4px solid ${severityColor}`,
                          borderRadius: 6,
                          padding: '12px 16px',
                          marginBottom: 10,
                          background: isApproved ? '#e8f5e9' : isRejected ? '#fce4ec' : isSelected ? '#e3f2fd' : '#fff',
                          opacity: (isApproved || isRejected) ? 0.7 : 1
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            {/* Selection checkbox — only shown for pending suggestions */}
                            {!isApproved && !isRejected && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSuggestionSelection(i)}
                                style={{ marginTop: 3, width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }}
                                title="Select this suggestion for bulk apply"
                              />
                            )}

                            <div style={{ flex: 1 }}>
                              {/* Badges + title */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 6px',
                                  borderRadius: 3, background: `${severityColor}20`, color: severityColor,
                                  textTransform: 'uppercase'
                                }}>
                                  {s.severity}
                                </span>
                                <span style={{
                                  fontSize: 10, fontWeight: 600, padding: '2px 6px',
                                  borderRadius: 3, background: '#e3f2fd', color: '#1565c0',
                                  textTransform: 'uppercase'
                                }}>
                                  {s.type}
                                </span>
                                <strong style={{ fontSize: 13 }}>{s.title}</strong>
                              </div>

                              {/* Description */}
                              <p style={{ fontSize: 12, color: '#555', margin: '4px 0 8px 0' }}>{s.description}</p>

                              {/* Rule detail pills — chain, action, protocol, ports */}
                              {(s.chain || s.action || s.protocol || s.sourceAddress || s.destinationAddress || s.sourcePort || s.destinationPort || s.connectionState) && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                                  {s.chain && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#f3e5f5', color: '#6a1b9a', borderRadius: 10, fontFamily: 'monospace' }}>
                                      chain: {s.chain}
                                    </span>
                                  )}
                                  {s.action && (
                                    <span style={{
                                      fontSize: 10, padding: '2px 7px', borderRadius: 10, fontFamily: 'monospace',
                                      background: s.action === 'accept' ? '#e8f5e9' : s.action === 'drop' || s.action === 'reject' ? '#ffebee' : '#fff3e0',
                                      color: s.action === 'accept' ? '#2e7d32' : s.action === 'drop' || s.action === 'reject' ? '#b71c1c' : '#e65100'
                                    }}>
                                      action: {s.action}
                                    </span>
                                  )}
                                  {s.protocol && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#e8eaf6', color: '#283593', borderRadius: 10, fontFamily: 'monospace' }}>
                                      proto: {s.protocol}
                                    </span>
                                  )}
                                  {s.sourceAddress && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#e0f2f1', color: '#00695c', borderRadius: 10, fontFamily: 'monospace' }}>
                                      src: {s.sourceAddress}{s.sourcePort ? `:${s.sourcePort}` : ''}
                                    </span>
                                  )}
                                  {s.destinationAddress && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#e0f2f1', color: '#00695c', borderRadius: 10, fontFamily: 'monospace' }}>
                                      dst: {s.destinationAddress}{s.destinationPort ? `:${s.destinationPort}` : ''}
                                    </span>
                                  )}
                                  {!s.sourceAddress && s.destinationPort && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#e0f2f1', color: '#00695c', borderRadius: 10, fontFamily: 'monospace' }}>
                                      dst-port: {s.destinationPort}
                                    </span>
                                  )}
                                  {s.connectionState && (
                                    <span style={{ fontSize: 10, padding: '2px 7px', background: '#fce4ec', color: '#880e4f', borderRadius: 10, fontFamily: 'monospace' }}>
                                      state: {s.connectionState}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Suggested RouterOS command */}
                              {s.suggestedRule && (
                                <pre style={{
                                  fontSize: 11, background: '#1e1e1e', color: '#d4d4d4', padding: '8px 12px',
                                  borderRadius: 4, marginTop: 6, overflowX: 'auto', whiteSpace: 'pre-wrap',
                                  fontFamily: "'Courier New', monospace"
                                }}>
                                  {s.suggestedRule}
                                </pre>
                              )}
                            </div>

                            {/* Per-suggestion Approve/Reject buttons */}
                            <div style={{ flexShrink: 0 }}>
                              {!isApproved && !isRejected && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleApproveSuggestion(s, i)}
                                    title="Approve this suggestion individually"
                                    style={{ fontSize: 11, padding: '4px 8px' }}
                                  >
                                    <FaCheck /> Approve
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => { setRejectingIndex(i); setRejectReason(''); }}
                                    title="Reject with reason"
                                    style={{ fontSize: 11, padding: '4px 8px' }}
                                  >
                                    <FaBan /> Reject
                                  </button>
                                </div>
                              )}
                              {isApproved && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#2e7d32', whiteSpace: 'nowrap' }}>✓ Approved</span>
                              )}
                              {isRejected && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#d32f2f', whiteSpace: 'nowrap' }}>✗ Rejected</span>
                              )}
                            </div>
                          </div>

                          {/* Reject reason input */}
                          {isRejecting && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                              <input
                                type="text"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Why is this suggestion not applicable?"
                                style={{ flex: 1, padding: '6px 10px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                                onKeyDown={(e) => e.key === 'Enter' && handleRejectSuggestion(s, i)}
                                autoFocus
                              />
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleRejectSuggestion(s, i)}
                                style={{ fontSize: 11 }}
                              >
                                Submit
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => { setRejectingIndex(null); setRejectReason(''); }}
                                style={{ fontSize: 11 }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 24, color: '#666' }}>
                    <FaCheck size={32} style={{ color: '#2e7d32', marginBottom: 8 }} />
                    <h4>No Suggestions</h4>
                    <p>The AI found no issues with your current firewall configuration.</p>
                  </div>
                )}
              </div>

              {/* Modal footer — bulk Apply Selected / Apply All */}
              {(aiResult.suggestions || []).length > 0 && (
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #dee2e6' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {selectedSuggestionIndices.size > 0
                      ? `${selectedSuggestionIndices.size} suggestion(s) selected`
                      : 'Select suggestions using the checkboxes, then apply them to all routers with safe mode.'}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleApplySuggestions(Array.from(selectedSuggestionIndices))}
                      disabled={applyingBulk || selectedSuggestionIndices.size === 0}
                      title={`Apply ${selectedSuggestionIndices.size} selected suggestion(s) to all provisioned routers with safe mode`}
                    >
                      {applyingBulk
                        ? <><FaSync className="fa-spin" /> Applying...</>
                        : <><FaShieldAlt /> Apply Selected ({selectedSuggestionIndices.size})</>}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleApplySuggestions(
                        (aiResult.suggestions || [])
                          .map((s, i) => ({ s, i }))
                          .filter(({ s }) => !s._approved && !s._rejected)
                          .map(({ i }) => i)
                      )}
                      disabled={applyingBulk || pendingSuggestions.length === 0}
                      title="Apply all pending suggestions to all provisioned routers with safe mode"
                    >
                      {applyingBulk
                        ? <><FaSync className="fa-spin" /> Applying...</>
                        : <><FaShieldAlt /> Apply All ({pendingSuggestions.length})</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RouterFirewallTemplatesPage;
